(ns machtfit.place-order
  (:require ["@adyen/adyen-web" :refer [AdyenCheckout Dropin Card PayPal Redirect]]
            [machtfit.api :as api]
            [machtfit.app.routes :as routes]
            [machtfit.core :as machtfit]
            [machtfit.startpage.core :as startpage]
            [machtfit.translation :refer [t]]
            [machtfit.widgets.box :as box]
            [machtfit.widgets.button :as button]
            [re-frame.core :as rf]
            [reitit.frontend]))

(defn- result []
  (let [payment-result @(rf/subscribe [:get [:payment-result]])]
    (case @(rf/subscribe [:get [:ui :place-order-state]])
      :failed [box/notification
               {:color :raspberrypink
                :content
                [:div.flex.gap-2.items-center.align-center
                 [:p.m-0
                  (t {:en "Payment failed: "
                      :de "Zahlung fehlgeschlagen: "})
                  [:strong.font-bold
                   (if (= payment-result "Refused")
                     (t {:en "Your payment was refused. Please check your payment details or try another payment method."
                         :de "Deine Zahlung wurde abgelehnt. Bitte überprüfe deine Zahlungsdaten oder versuche eine andere Zahlungsmethode."})
                     payment-result)]]
                 [button/button
                  {:class "ml-4"
                   :on-click #(rf/dispatch [::try-again-clicked])
                   :size :sm
                   :hierarchy :destructive-primary
                   :text (t {:en "Try again"
                             :de "Nochmal versuchen"})}]]}]
      :success [:p (t {:en "Payment successful."
                       :de "Zahlung erfolgt."})]
      nil)))

(defn place-order []
  [:div.container {:style {:min-height "500px" :position "relative"}}
   [:div.py-8
    (when (nil? @(rf/subscribe [:get [:ui :place-order-state]]))
      [:div {:style {:position "absolute"
                     :height "100%"
                     :display "flex"
                     :justify-content "center"
                     :align-items "center"
                     :width "100%"}}
       [:img {:src (machtfit/static "img/machtfit-supersign-loading.gif")}]])
    [:div#adyen-dropin {:style {:position "relative"
                                :background-color "white"}}]
    [result]]])

(rf/reg-event-fx ::try-again-clicked
  (fn [{:keys [db]} _]
    {:redirect {:url (-> db :urls :basket:summary)}}))

(defn- failure->db [db reason]
  (-> db
      (assoc :payment-result reason)
      (assoc-in [:ui :place-order-state] :failed)))

(defn- success->db [db]
  (-> db
      (assoc-in [:ui :place-order-state] :success)))

(rf/reg-event-fx ::payment-error
  (fn [{:keys [db]} [_ error]]
    ;; v6: PayPal cancellations trigger onError with type "CANCEL" - treat as recoverable
    (let [error-type (when error (.-type error))]
      (if (= error-type "CANCEL")
        (do
          (js/console.log ::payment-cancelled "User cancelled payment")
          {:db db})
        (do
          (js/console.error ::payment-error error)
          {:db (failure->db db (or (when error (.-message error)) "Payment error"))})))))

(rf/reg-event-fx ::payment-complete
  (fn [{:keys [db]} [_ _response]]
    ;; v6: onPaymentCompleted only fires for successful payments
    {:db (success->db db)
     :redirect {:url (:thank-you-url db)}}))

(rf/reg-event-fx ::payment-failed
  (fn [{:keys [db]} [_ {result-code :resultCode
                        :as response}]]
    ;; v6: onPaymentFailed handles all payment failures
    (when (not= result-code "Refused")
      (js/console.error ::payment-failed-response response))
    {:db (failure->db db result-code)}))

(defn- make-adyen-checkout [live? client-key id session-data shopper-locale]
  (AdyenCheckout (clj->js (cond-> {:environment (if live? "live" "test")
                                   :clientKey client-key
                                   :analytics {:enabled false}
                                   :session (cond-> {:id id}
                                              session-data (assoc :sessionData session-data))
                                   :onPaymentCompleted #(rf/dispatch [::payment-complete (js->clj % :keywordize-keys true)])
                                   :onPaymentFailed #(rf/dispatch [::payment-failed (js->clj % :keywordize-keys true)])
                                   :onError #(rf/dispatch [::payment-error %])}
                            shopper-locale (assoc :locale shopper-locale)))))

(def ^:private payment-method-components
  "Payment method components required by Drop-in v6 for tree-shaking support.
   IMPORTANT: This list must include all payment methods enabled in the Adyen
   configuration. When adding/enabling payment methods, update this array with
   the corresponding component imports (e.g., Card, PayPal, Redirect, etc.)."
  #js [Card PayPal Redirect])

(rf/reg-fx ::pay
  (fn [[live? client-key {:keys [id session-data shopper-locale]}]]
    (-> (make-adyen-checkout live? client-key id session-data shopper-locale)
        (.then (fn [checkout]
                 (-> (Dropin. checkout #js {:paymentMethodComponents payment-method-components})
                     (.mount "#adyen-dropin"))))
        (.catch (fn [error] (js/console.error error))))))

(rf/reg-fx ::finalize-payment
  (fn [[live? client-key session-id redirect-result]]
    (-> (make-adyen-checkout live? client-key session-id nil nil)
        (.then (fn [checkout] (.submitDetails ^js checkout (clj->js {:details {:redirectResult redirect-result}})))))))

(rf/reg-event-fx ::api-success
  (fn [{:keys [db]} [_ live? response]]
    (let [{result-data :result
           reason :reason
           payment-session :payment-session} (get-in response [:result :place-order :result])]
      (case result-data
        "succeeded" {:db (success->db db)
                     :redirect {:url (:thank-you-url db)}}
        "failed" {:db (failure->db db reason)}
        "payment-required" {:db (assoc-in db [:ui :place-order-state] :pending)
                            ::pay [live? (:client-key db) payment-session]}
        {:db (failure->db db result-data)}))))

(rf/reg-event-fx ::api-error
  (fn [{:keys [db]} [_ error]]
    (js/console.error ::api-error error)
    {:db (failure->db db "API error")}))

(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ {:keys [client-key live?] :as js-data} basket-id session-id redirect-result]]
    (let [db (merge db js-data)
          place-order-request
          (api/bare-request db {:on-error #(rf/dispatch [::api-error %])
                                :data `[(:place-order {:basket_id ~basket-id} :place-order)]
                                :on-success #(rf/dispatch [::api-success live? %])})]
      (cond-> {:db db}
        (not session-id) (assoc :post place-order-request)
        session-id (assoc ::finalize-payment [live? client-key session-id redirect-result])))))

(defn ^:export load [js-data]
  (startpage/set-messages!)
  (let [js-data (js->clj js-data :keywordize-keys true)
        {:keys [path-params query-params]} (reitit.frontend/match-by-path routes/router (str js/location.pathname js/location.search))
        {session-id :sessionId
         redirect-result :redirectResult} query-params]
    (rf/dispatch [::init js-data (:basket-id path-params) session-id redirect-result])))
